import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import '../../styles/admin-dashboard.css';
import { IcoSettings, IcoCheck } from '../dashboard/DashIcons';

const PLAN_INFO = {
  starter: {
    name:         'Starter',
    baseFee:      29,
    pricePerSeat: 0,
    softLimit:    30,
    maxEmployees: 34,
    color:        '#22c55e',
    bg:           '#eff6ff',
    border:       '#bfdbfe',
    features:     ['Up to 30 employees (hard cap 34)', 'Clock in / clock out', 'Time tracking & entry', 'Scheduling', 'PTO tracking', 'Payroll processing', 'Mobile app', 'Email support'],
  },
  professional: {
    name:         'Professional',
    baseFee:      69,
    pricePerSeat: 0,
    softLimit:    100,
    maxEmployees: 115,
    color:        '#22c55e',
    bg:           '#eef2ff',
    border:       '#c7d2fe',
    features:     ['Up to 100 employees (hard cap 115)', 'Everything in Starter', 'Reporting & analytics', 'EVV compliance', 'Labor cost exports', 'Priority support'],
  },
  growth: {
    name:         'Growth',
    baseFee:      149,
    pricePerSeat: 0,
    softLimit:    150,
    maxEmployees: 172,
    color:        '#7c3aed',
    bg:           '#f5f3ff',
    border:       '#c4b5fd',
    features:     ['Up to 150 employees (hard cap 172)', 'Everything in Professional', 'Multi-location support', 'Priority support'],
  },
  enterprise: {
    name:         'Enterprise',
    baseFee:      null,
    pricePerSeat: null,
    softLimit:    99999,
    maxEmployees: 99999,
    color:        '#0f172a',
    bg:           '#f8fafc',
    border:       '#cbd5e1',
    features:     ['Unlimited employees', 'Everything in Growth', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee', 'Custom onboarding'],
  },
};

const STATUS_BADGE = {
  active: { label: 'Active', color: '#15803d', bg: '#f0fdf4' },
  trial: { label: 'Free Trial', color: '#d97706', bg: '#fffbeb' },
  cancelled: { label: 'Cancelled', color: '#dc2626', bg: '#fef2f2' },
  suspended: { label: 'Suspended', color: '#dc2626', bg: '#fef2f2' },
  inactive: { label: 'Inactive', color: '#6b7280', bg: '#f3f4f6' },
};

export default function SubscriptionPage() {
  const { user } = useAuth();
  const token = user?.token;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/subscriptions/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setStatus(await res.json());
    } catch (err) {
      console.error('Error fetching subscription status:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setMessage({ type: 'success', text: 'Subscription updated successfully!' });
    } else if (params.get('cancelled') === 'true') {
      setMessage({ type: 'error', text: 'Checkout was cancelled. No changes were made.' });
    }
  }, [fetchStatus]);

  const handleUpgrade = async (planKey) => {
    setActionLoading(planKey);
    try {
      const res = await fetch('/api/subscriptions/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ planKey })
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        setMessage({ type: 'error', text: data.message || 'Error starting checkout' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error connecting to billing' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleBillingPortal = async () => {
    setActionLoading('portal');
    try {
      const res = await fetch('/api/subscriptions/billing-portal', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        setMessage({ type: 'error', text: data.message || 'Error opening billing portal' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error connecting to billing' });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <p style={{ color: '#6b7280', padding: '3rem', textAlign: 'center' }}>Loading subscription info...</p>
      </div>
    </div>
  );

  const currentPlan      = status?.plan || 'starter';
  const badge            = STATUS_BADGE[status?.status] || STATUS_BADGE.inactive;
  const trialDaysLeft    = status?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(status.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;
  const seats            = status?.employeeCount || 0;
  const baseFee          = status?.baseFee          ?? PLAN_INFO[currentPlan]?.baseFee          ?? 0;
  const pricePerSeat     = status?.pricePerSeat     ?? PLAN_INFO[currentPlan]?.pricePerSeat     ?? 0;
  const estimatedMonthly = status?.estimatedMonthly ?? (baseFee + seats * pricePerSeat);

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <section className="welcome-section">
          <h1>Subscription</h1>
          <p className="subtitle">Manage your plan, billing, and usage</p>
        </section>

        {message && (
          <div style={{ padding: '1rem 1.25rem', borderRadius: '8px', marginBottom: '1.5rem', fontWeight: '600', background: message.type === 'success' ? '#d1fae5' : '#fee2e2', color: message.type === 'success' ? '#065f46' : '#991b1b' }}>
            {message.text}
          </div>
        )}

        {/* Current Plan Card */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
              <div>
                <p style={{ fontSize: '0.825rem', color: '#9ca3af', margin: '0 0 0.25rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Plan</p>
                <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '800', color: '#111827' }}>
                  {PLAN_INFO[currentPlan]?.name}
                </h2>
                <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
                  {baseFee !== null ? `$${baseFee}/mo + $${pricePerSeat}/employee` : 'Custom pricing'}
                </p>
              </div>
              <span style={{ padding: '0.35rem 0.875rem', borderRadius: '99px', fontSize: '0.8rem', fontWeight: '700', background: badge.bg, color: badge.color }}>
                {badge.label}
              </span>
            </div>

            {status?.status === 'trial' && trialDaysLeft !== null && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400e', fontWeight: '600' }}>
                  ⏳ {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left in your free trial
                </p>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.775rem', color: '#92400e' }}>
                  Subscribe before your trial ends to keep access to all features.
                </p>
              </div>
            )}

            {status?.stripeCustomerId && (
              <button
                onClick={handleBillingPortal}
                disabled={actionLoading === 'portal'}
                style={{ width: '100%', padding: '0.75rem', background: '#f3f4f6', color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
              >
                {actionLoading === 'portal' ? 'Opening...' : <><IcoSettings size={14}/> Manage Billing & Invoices</>}
              </button>
            )}
          </div>

          {/* Usage Card */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <p style={{ fontSize: '0.825rem', color: '#9ca3af', margin: '0 0 1rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Usage</p>

            {/* Seat breakdown */}
            <div style={{ padding: '1rem', background: '#f0f4ff', borderRadius: '10px', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#374151' }}>Active Employees</span>
                <span style={{ fontSize: '1.5rem', fontWeight: '800', color: '#4338ca' }}>{seats}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '0.825rem', color: '#6b7280' }}>Base fee</span>
                <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '600' }}>{baseFee !== null ? `$${baseFee}/mo` : 'Custom'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.825rem', color: '#6b7280' }}>Per employee</span>
                <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '600' }}>{pricePerSeat !== null ? `$${pricePerSeat}/mo` : '—'}</span>
              </div>
              <div style={{ borderTop: '1px solid #c7d2fe', paddingTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#374151' }}>Estimated this month</span>
                <span style={{ fontSize: '1.1rem', fontWeight: '800', color: '#4338ca' }}>${estimatedMonthly}/mo</span>
              </div>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.72rem', color: '#6b7280' }}>
                Base fee + ${pricePerSeat} × {seats} employee{seats !== 1 ? 's' : ''}. Adjusts automatically.
              </p>
            </div>

            <div>
              <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>Included Features</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {Object.entries(status?.features || {}).map(([key, enabled]) => (
                  <span key={key} style={{
                    padding: '0.2rem 0.6rem',
                    borderRadius: '99px',
                    fontSize: '0.72rem',
                    fontWeight: '600',
                    background: enabled ? '#e0e7ff' : '#f3f4f6',
                    color: enabled ? '#4338ca' : '#d1d5db',
                    textDecoration: enabled ? 'none' : 'line-through'
                  }}>
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Plan Selection */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>Available Plans</h2>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Upgrade or downgrade anytime. Changes take effect immediately.</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
            {Object.entries(PLAN_INFO).map(([key, plan]) => {
              const isCurrent = key === currentPlan;
              const isHigher = Object.keys(PLAN_INFO).indexOf(key) > Object.keys(PLAN_INFO).indexOf(currentPlan);

              return (
                <div key={key} style={{
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: `2px solid ${isCurrent ? plan.border : '#e5e7eb'}`,
                  background: isCurrent ? plan.bg : 'white',
                  position: 'relative'
                }}>
                  {key === 'professional' && (
                    <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #22c55e, #0f4d2e)', color: 'white', padding: '0.2rem 0.875rem', borderRadius: '99px', fontSize: '0.72rem', fontWeight: '700', whiteSpace: 'nowrap' }}>
                      MOST POPULAR
                    </div>
                  )}

                  <h3 style={{ margin: '0 0 0.15rem', fontWeight: '800', color: '#111827' }}>{plan.name}</h3>
                  {plan.baseFee !== null ? (
                    <>
                      <p style={{ margin: '0 0 0.1rem', fontSize: '1.25rem', fontWeight: '700', color: plan.color }}>
                        ${plan.baseFee}/mo <span style={{ fontSize: '0.825rem', fontWeight: '500', color: '#6b7280' }}>+ ${plan.pricePerSeat}/employee</span>
                      </p>
                      <p style={{ margin: '0 0 1rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                        {seats > 0 ? `≈ $${plan.baseFee + seats * plan.pricePerSeat}/mo for your ${seats} employee${seats !== 1 ? 's' : ''}` : 'Base fee + pay per active employee'}
                      </p>
                    </>
                  ) : (
                    <>
                      <p style={{ margin: '0 0 0.1rem', fontSize: '1.25rem', fontWeight: '700', color: plan.color }}>Custom pricing</p>
                      <p style={{ margin: '0 0 1rem', fontSize: '0.75rem', color: '#9ca3af' }}>Contact us for a quote</p>
                    </>
                  )}

                  <ul style={{ margin: '0 0 1.25rem', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {plan.features.map((f, i) => (
                      <li key={i} style={{ fontSize: '0.825rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <IcoCheck size={12} color={plan.color}/> {f}
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <div style={{ padding: '0.6rem', background: plan.bg, border: `1px solid ${plan.border}`, borderRadius: '6px', textAlign: 'center', fontSize: '0.825rem', fontWeight: '700', color: plan.color }}>
                      <><IcoCheck size={12} color="#22c55e"/> Current Plan</>
                    </div>
                  ) : key === 'enterprise' ? (
                    <a
                      href="mailto:contact@flurrysystems.com?subject=Enterprise Plan Inquiry"
                      style={{
                        display: 'block', width: '100%', padding: '0.65rem', boxSizing: 'border-box',
                        background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                        color: 'white', border: 'none', borderRadius: '8px',
                        fontWeight: '700', fontSize: '0.875rem', textAlign: 'center', textDecoration: 'none',
                      }}
                    >
                      Contact Us
                    </a>
                  ) : (
                    <button
                      onClick={() => handleUpgrade(key)}
                      disabled={!!actionLoading}
                      style={{
                        width: '100%', padding: '0.65rem',
                        background: actionLoading === key ? '#e5e7eb' : isHigher ? 'linear-gradient(135deg, #22c55e, #0f4d2e)' : '#f3f4f6',
                        color: isHigher ? 'white' : '#374151',
                        border: 'none', borderRadius: '8px', cursor: actionLoading ? 'not-allowed' : 'pointer',
                        fontWeight: '700', fontSize: '0.875rem'
                      }}
                    >
                      {actionLoading === key ? '⏳ Loading...' : isHigher ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}